import React from "react";
import { TimelineDot } from "./TimelineDot";

interface TimelineIndicatorProps {
    items: Array<any>;
    currentIndex: number;
    scrollProgress: number;
    orientation?: 'vertical' | 'horizontal';
    position?: 'left' | 'right' | 'center';
    className?: string;
    showProgress?: boolean;
    activeColor?: string;
    inactiveColor?: string;
    dotSize?: 'small' | 'medium' | 'large';
}

export const TimelineIndicator: React.FC<TimelineIndicatorProps> = React.memo(({
    items,
    currentIndex,
    scrollProgress,
    orientation = 'vertical',
    position = 'left',
    className = '',
    showProgress = true,
    activeColor = '#899B30',
    inactiveColor = '#E3DBC8',
    dotSize = 'medium'
}) => {
    const isVertical = orientation === 'vertical';
    const totalHeight = items.length * 60; // 60px spacing between dots
    
    // Smooth the progress calculation to reduce flickering
    const smoothProgress = React.useMemo(() => {
        const clampedScrollProgress = Math.max(0, Math.min(1, scrollProgress));
        const clampedIndex = Math.max(0, Math.min(items.length - 1, currentIndex));
        return ((clampedIndex + clampedScrollProgress) / items.length) * 100;
    }, [currentIndex, scrollProgress, items.length]);
    
    const progressPercentage = Math.max(0, Math.min(100, smoothProgress));

    const containerClasses = [
        'timeline-indicator',
        isVertical ? 'flex-col' : 'flex-row',
        'flex items-center relative',
        className
    ].join(' ');

    const lineClasses = isVertical ? 'w-[2px] h-full' : 'h-[2px] w-full';
    const progressClasses = [
        'absolute top-0 left-0 transition-all duration-500 ease-out',
        isVertical ? 'w-full' : 'h-full'
    ].join(' ');

    const progressStyle = isVertical 
        ? { height: `${Math.min(100, Math.max(0, progressPercentage))}%` }
        : { width: `${Math.min(100, Math.max(0, progressPercentage))}%` };

    const dotsContainerClasses = [
        'absolute flex',
        isVertical ? 'flex-col top-0 left-1/2 transform -translate-x-1/2' : 'flex-row left-0 top-1/2 transform -translate-y-1/2',
    ].join(' ');

    const dotsStyle = isVertical 
        ? { gap: '48px' } 
        : { gap: '48px' };

    return (
        <div className={containerClasses}>
            <div className="relative">
                {/* Timeline line */}
                <div 
                    className={`${lineClasses} relative`} 
                    style={isVertical ? { height: `${totalHeight}px` } : { width: `${totalHeight}px` }}
                >
                    <div className={`w-full h-full`} style={{ backgroundColor: inactiveColor }} />
                    
                    {/* Progress indicator */}
                    {showProgress && (
                        <div 
                            className={progressClasses}
                            style={{
                                backgroundColor: activeColor,
                                ...progressStyle,
                                willChange: 'height, width',
                                transform: 'translateZ(0)' // Force hardware acceleration
                            }}
                        />
                    )}
                </div>
                
                {/* Timeline dots */}
                <div className={dotsContainerClasses} style={dotsStyle}>
                    {items.map((_, index) => (
                        <TimelineDot
                            key={index}
                            isActive={index <= currentIndex}
                            index={index}
                            size={dotSize}
                            activeColor={activeColor}
                            showTrail={index === currentIndex}
                            trailProgress={scrollProgress * 100}
                            className="relative z-10"
                        />
                    ))}
                </div>
            </div>
        </div>
    );
});

export default TimelineIndicator;
